import { createSlice } from '@reduxjs/toolkit';
import { toTron } from '../common/utils';
import logger from '../utils/logger';
import {
  createRefferal,
  getAccountV2,
  getCanDelegatedMaxSize,
  getMyFreezeList,
  getTransactionResource,
  tronscanApiCall,
} from '../common/api';
import {
  ACCOUNT_DETAILS,
  BANDWIDTH,
  ENERGY,
} from '../common/utils/constants';

const initialState = {
  isLoading: false,
  accountDetails: null,
  accountAddress: '',
  accountBalance: '',
  referralAddress: '',
  noWalletDetect: false,
  totalFrozenBalance: 0,
  resourceDetails: null,
  isDataLoading: false,
  isAdmin: false,
  tronResources: null,
  myFreezeList: [],
  isDataLoaded: false,
  walletActivated: false,
};

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setIsLoading(state, action) {
      state.isLoading = action.payload;
    },
    setIsDataLoading(state, action) {
      state.isDataLoading = action.payload;
    },
    setAccountDetails(state, action) {
      state.accountDetails = action.payload;
    },
    setAccountAddress(state, action) {
      state.accountAddress = action.payload;
    },
    setAccountBalance(state, action) {
      state.accountBalance = action.payload;
    },
    setReferralAddress(state, action) {
      state.referralAddress = action.payload;
    },
    setNoWalletDetect(state, action) {
      state.noWalletDetect = action.payload;
    },
    setTotalFrozenBalance(state, action) {
      state.totalFrozenBalance = action.payload;
    },
    setResourceDetails(state, action) {
      state.resourceDetails = action.payload;
    },
    setIsAdmin(state, action) {
      state.isAdmin = action.payload;
    },
    setTronResources(state, action) {
      state.tronResources = action.payload;
    },
    setMyFreezeList(state, action) {
      state.myFreezeList = action.payload;
    },
    setIsDataLoaded(state, action) {
      state.isDataLoaded = action.payload;
    },
    setWalletActivated(state, action) {
      state.walletActivated = action.payload;
    },
  },
});

export const {
  setIsLoading,
  setAccountDetails,
  setAccountAddress,
  setAccountBalance,
  setReferralAddress,
  setNoWalletDetect,
  setTotalFrozenBalance,
  setResourceDetails,
  setIsDataLoading,
  setIsAdmin,
  setTronResources,
  setMyFreezeList,
  setIsDataLoaded,
  setWalletActivated,
} = accountSlice.actions;

export default accountSlice.reducer;

export const getBalance = () => async (dispatch) => {
  try {
    dispatch(setIsLoading(true));
    //if wallet installed and logged , getting TRX token balance
    if (window?.tronWeb && window?.tronWeb?.ready) {
      let walletBalances = await window.tronWeb.trx.getAccount(
        window.tronWeb.defaultAddress.base58
      );
      dispatch(setIsLoading(false));
      return walletBalances;
    } else {
      return 0;
    }
  } catch (error) {
    dispatch(setIsLoading(false));
  }
};

export const getWalletDetails = () => async (dispatch) => {
  try {
    dispatch(setIsDataLoading(true));
    
    // Check if TronWeb is available
    if (window.tronWeb) {
      dispatch(setNoWalletDetect(false));
      
      // Check if wallet is ready and has an address
      if (window.tronWeb.ready && window.tronWeb.defaultAddress && window.tronWeb.defaultAddress.base58) {
        const tempAddress = window.tronWeb.defaultAddress.base58;
        
        // Update account address immediately
        dispatch(setAccountAddress(tempAddress));
        localStorage.setItem('address', tempAddress);

        // Get balance
        try {
          const balanceResult = await dispatch(getBalance());
          if (balanceResult) {
            dispatch(setAccountBalance(toTron(balanceResult.balance)));
          }
        } catch (balanceError) {
          logger.error('Failed to get balance:', balanceError);
        }

        dispatch(setIsDataLoading(false));
        dispatch(setIsDataLoaded(true));
        dispatch(setWalletActivated(true));

        // Handle referral
        const urlParams = new URLSearchParams(window.location.search);
        const referralAddress = urlParams.get('ref');
        if (referralAddress) {
          try {
            await createRefferal(tempAddress, referralAddress);
          } catch (referralError) {
            logger.error('Failed to create referral:', referralError);
          }
        }
      } else {
        // Wallet detected but not connected/ready
        dispatch(setAccountAddress(''));
        dispatch(setAccountBalance(0));
        dispatch(setWalletActivated(false));
        dispatch(setIsDataLoading(false));
        localStorage.removeItem('address');
      }
    } else {
      // No wallet detected
      dispatch(setNoWalletDetect(true));
      dispatch(setAccountAddress(''));
      dispatch(setAccountBalance(0));
      dispatch(setWalletActivated(false));
      dispatch(setIsDataLoading(false));
      localStorage.removeItem('address');
    }
  } catch (error) {
    logger.error('Error in getWalletDetails:', error);
    dispatch(setIsDataLoading(false));
  }
};

export const handleAdminReferral =
  (userAddress, referralAddress) => async (dispatch) => {
    try {
      if (!referralAddress) {
        referralAddress = 'TRCy8BponCNzrs8sN8YV48EEKVvcZM8tNn';
      }
      await createRefferal(userAddress, referralAddress);
    } catch (error) {
      logger.error('Failed to handle admin referral:', error);
    }
  };

export const fetchAccountDetails = () => async (dispatch, getState) => {
  try {
    dispatch(setIsLoading(true));
    const accountAddress = getState().account.accountAddress;
    // Use tronscanApiCall which automatically adds the API key for Tronscan URLs
    const res = await tronscanApiCall(
      ACCOUNT_DETAILS + `?address=${accountAddress}`
    );
    const res2 = await getAccountV2(accountAddress);

    // Try to get freeze lists, but don't fail if API key is invalid
    let myFreezeList = [];
    let myReceivedList = [];
    try {
      myFreezeList = await getMyFreezeList(accountAddress, 2);
      myReceivedList = await getMyFreezeList(accountAddress, 1);
    } catch (freezeError) {
      logger.warn('Failed to fetch freeze lists (API key may be invalid):', freezeError);
    }

    if (res2.status === 200 && res2.data.activated) {
      dispatch(setWalletActivated(true));
      let totalDelegateEnergy = 0;
      let totalDelegateBandwidth = 0;
      let totalReceivedEnergy = 0;
      let totalReceivedBandwidth = 0;
      for (let i = 0; i < myFreezeList.length; i++) {
        if (myFreezeList[i].resource === 1) {
          totalDelegateEnergy += myFreezeList[i].resourceValue;
        } else if (myFreezeList[i].resource === 0) {
          totalDelegateBandwidth += myFreezeList[i].resourceValue;
        }
      }
      for (let i = 0; i < myReceivedList.length; i++) {
        if (myReceivedList[i].resource === 1) {
          totalReceivedEnergy += myReceivedList[i].resourceValue;
        } else if (myReceivedList[i].resource === 0) {
          totalReceivedBandwidth += myReceivedList[i].resourceValue;
        }
      }
      const {
        totalEnergyLimit,
        totalEnergyWeight,
        totalNetLimit,
        totalNetWeight,
      } = res.data.bandwidth;
      let total_energy_limit = totalEnergyLimit;
      let total_net_limit = totalNetLimit;
      let total_energy_weight = totalEnergyWeight;
      let total_net_weight = totalNetWeight;
      let NetUsed = 0;
      let NetLimit = 0;
      let EnergyLimit = 0;

      if (res.data.NetUsed) {
        NetUsed = res.data.NetUsed;
      }
      if (res.data.NetLimit) {
        NetLimit = res.data.NetLimit;
      }
      if (res.data.EnergyLimit) {
        EnergyLimit = res.data.EnergyLimit;
      }

      let EnergyAmountAvailable = 0;
      let BandAmountAvailable = 0;

      try {
        let EnergyAvailableForDelegation = await getCanDelegatedMaxSize(
          accountAddress,
          ENERGY
        );
        let BandwidthAvailableForDelegation = await getCanDelegatedMaxSize(
          accountAddress,
          BANDWIDTH
        );

        EnergyAmountAvailable =
          (window.tronWeb.fromSun(EnergyAvailableForDelegation.max_size) *
            total_energy_limit) /
          total_energy_weight;
        BandAmountAvailable =
          (window.tronWeb.fromSun(BandwidthAvailableForDelegation.max_size) *
            total_net_limit) /
          total_net_weight;
      } catch (delegationError) {
        logger.warn('Failed to get delegation info (non-critical):', delegationError);
      }

      dispatch(
        setAccountDetails({
          ...res2.data,
          maxDelegatableBandwidth: BandAmountAvailable,
          maxDelegatableEnergy: EnergyAmountAvailable,
          totalDelegateEnergy: totalDelegateEnergy,
          totalDelegateBandwidth: totalDelegateBandwidth,
          totalReceivedEnergy: totalReceivedEnergy,
          totalReceivedBandwidth: totalReceivedBandwidth,
        })
      );

      const accountBalanceValue = toTron(res.data.balance);
      const totalFrozenValue = toTron(res2.data.totalFrozen + res2.data.totalFrozenV2);

      dispatch(setAccountBalance(accountBalanceValue));
      dispatch(setTotalFrozenBalance(totalFrozenValue));
      dispatch(setResourceDetails(res2.data.bandwidth));
      dispatch(setIsLoading(false));
    } else {
      logger.warn('Wallet not activated or failed to fetch:', res2.status, res2.data);
      dispatch(setIsLoading(false));
    }
  } catch (error) {
    logger.error('Failed to fetch account details:', error);
    dispatch(setIsLoading(false));
  }
};

export const getTransactionResourceHandler =
  () => async (dispatch, getState) => {
    try {
      const res = await getTransactionResource();
      dispatch(setTronResources(res));
    } catch (error) {
      logger.error('Failed to get transaction resource:', error);
    }
  };
export const checkAdmin = () => async (dispatch, getState) => {
  try {
    const accountAddress = getState().account.accountAddress;
    accountAddress === process.env.REACT_APP_ADMIN_ADDRESS
      ? dispatch(setIsAdmin(true))
      : dispatch(setIsAdmin(false));
  } catch (error) {
    logger.error('Failed to check admin status:', error);
  }
};
